import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  RefreshCw, Search, Settings, CheckCircle2, Lock, AlertTriangle, ShieldCheck, ShieldAlert,
  PauseCircle, ChevronLeft, ChevronRight, X, Mail, Download, Play, Copy, Check,
  ArrowRight, Clock3, Hand, Send, ArrowRightLeft,
} from 'lucide-react';
import { STANDARD_PHASES, StandardPhaseKey } from '../../../types/programPhases';
import { safeFetch } from '../../../lib/api';
import { auth } from '../../../lib/firebase';
import { workspaceHeader } from '../../../lib/workspaces';
import { notifyToast } from '../../../lib/toastStore';
import { usePrograms } from '../../../hooks/usePrograms';
import { STAGE_ORDER, STAGE_LABELS } from '../../../lib/workflow/transitions';
import { StatusSignal } from './StatusSignal';
import { ApplicantDrawer, BulkNudgeModal } from './ApplicantDrawer';
import { BulkAdvanceModal } from './BulkAdvanceModal';
import { WorkflowSettingsPanel } from './WorkflowSettingsPanel';
import { AssignAutomationModal } from './AssignAutomationModal';
import { Sparkles } from 'lucide-react';
import { InternalTasksSection } from './InternalTasksSection';
import { RedlistSection } from './RedlistSection';
import type { WorkflowRecord, WorkflowSummary, QueueKey } from './types';
import { QUEUE_LABELS } from './types';
import { buildPipelineSegments } from './pipelineSegments';
import { pageSelectionShare } from '../batchSelection';
import SearchableSelect from '../../shared/SearchableSelect';
import UnifiedApplicantFilterBar from '../../shared/UnifiedApplicantFilterBar';
import { ApplicantFilterState, DEFAULT_FILTER_STATE, filterApplicantRecord, hasActiveFilters, excludeFilterCriteria, type FilterCriterionKey } from '../../../lib/applicantFilterUtils';

interface WorkflowPageProps {
  onViewApplicant?: (app: any) => void;
}

const PAGE_SIZE = 50;

/**
 * Unified-filter criteria the Workflow page does NOT offer: its records
 * (GET /api/admin/workflow/v2/records) carry no gender, application
 * status, tags, placements, program term, or created/submitted
 * timestamps, so those criteria could only ever return zero records —
 * or, for placement, derive the same value for every record. The bar
 * hides them and the engine call strips them, so they can never filter
 * on this screen. Supported here: Workflow Phase (stage), Country,
 * Financial Station (balanceDue), Program, Batches (batch applicantIds
 * membership), Referrer (referrerId), Assigned Team Member (ownerUid).
 */
const WORKFLOW_HIDDEN_CRITERIA: FilterCriterionKey[] = [
  'sponsor',
  'gender',
  'status',
  'tags',
  'placementStatus',
  'term',
  'createdRange',
  'submittedRange',
];

/* ── helpers ─────────────────────────────────────────────────────────── */

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('') || '?';
}

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const diff = t - Date.now();
  const abs = Math.abs(diff);
  const mins = Math.round(abs / 60000);
  const hrs = Math.round(abs / 3600000);
  const days = Math.round(abs / 86400000);
  const s = mins < 60 ? `${mins}m` : hrs < 48 ? `${hrs}h` : `${days}d`;
  return diff >= 0 ? `in ${s}` : `${s} ago`;
}

function fmtTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * Client-side safety net (PR-20): one application must never render
 * twice in the hub table. The server dedupes by applicationId; this
 * keeps the guarantee even against a stale/older response payload.
 * First occurrence wins (server ordering is authoritative).
 */
function dedupeWorkflowRows(rows: WorkflowRecord[]): WorkflowRecord[] {
  const seen = new Set<string>();
  const out: WorkflowRecord[] = [];
  for (const r of rows || []) {
    const id = String(r.applicationId || '').trim();
    if (id) {
      if (seen.has(id)) continue;
      seen.add(id);
    }
    out.push(r);
  }
  return out;
}

/**
 * Ready-to-Matriculate animated check. Circle scales in, check draws, one
 * soft ring pulse, then static (spec §12.8). Parent tracks animated ids so
 * this plays once per session per applicant.
 */
function ReadyCheck({ uid, animatedIds }: { uid: string; animatedIds: React.MutableRefObject<Set<string>> }) {
  const animate = !animatedIds.current.has(uid);
  useEffect(() => {
    animatedIds.current.add(uid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);
  return (
    <span className="relative inline-flex" role="img" aria-label="Ready to matriculate">
      <svg width="22" height="22" viewBox="0 0 24 24" className={animate ? 'rtm-check-circle' : ''}>
        <circle cx="12" cy="12" r="10" fill="#10b981" />
        <path
          d="M8 12.5l2.6 2.6L16 9.5"
          fill="none"
          stroke="#fff"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray="24"
          className={animate ? 'rtm-check-path' : ''}
        />
      </svg>
      {animate && <span className="rtm-ring absolute inset-0 rounded-full border-2 border-emerald-400" aria-hidden="true" />}
    </span>
  );
}

interface SearchableMultiSelectProps {
  values: string[];
  onChange: (values: string[]) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  emptyLabel?: string;
  ariaLabel?: string;
}

function SearchableMultiSelect({
  values,
  onChange,
  options,
  placeholder = 'Select batches…',
  emptyLabel = 'No batches found',
  ariaLabel = 'Filter by batch',
}: SearchableMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(q.trim().toLowerCase())
  );

  const toggle = (val: string) => {
    if (blurTimer.current) clearTimeout(blurTimer.current);
    if (values.includes(val)) {
      onChange(values.filter((v) => v !== val));
    } else {
      onChange([...values, val]);
    }
  };

  const closeSoon = () => {
    if (blurTimer.current) clearTimeout(blurTimer.current);
    blurTimer.current = setTimeout(() => setOpen(false), 200);
  };

  const labelText =
    values.length === 0
      ? ''
      : values.length === 1
      ? options.find((o) => o.value === values[0])?.label || '1 Batch'
      : `${values.length} Batches Selected`;

  return (
    <div className="relative">
      <div className="relative">
        <Search className="h-3.5 w-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          type="text"
          value={open ? q : labelText}
          placeholder={placeholder}
          aria-label={ariaLabel}
          onFocus={() => { setOpen(true); setQ(''); }}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onBlur={closeSoon}
          className="w-full pl-9 pr-8 py-2 text-xs font-semibold border border-slate-200/80 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 text-slate-800"
        />
        {values.length > 0 ? (
          <button
            type="button"
            tabIndex={-1}
            onMouseDown={(e) => { e.preventDefault(); onChange([]); setQ(''); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-600 rounded cursor-pointer"
            title="Clear batch selection"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none">▾</span>
        )}
      </div>

      {open && (
        <div className="absolute z-30 mt-1 w-full min-w-[220px] bg-white border border-slate-200/80 rounded-xl shadow-xl max-h-56 overflow-y-auto overscroll-contain">
          {values.length > 0 && (
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onChange([]); }}
              className="w-full text-left px-3 py-2 text-xs font-bold text-slate-500 hover:bg-slate-50 border-b border-slate-100 cursor-pointer"
            >
              Clear All ({values.length})
            </button>
          )}
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-500">{emptyLabel}</div>
          ) : (
            filtered.map((o) => {
              const selected = values.includes(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); toggle(o.value); }}
                  className={`w-full text-left px-3 py-2 text-xs font-medium hover:bg-blue-50 flex items-center justify-between gap-2 cursor-pointer ${
                    selected ? 'bg-blue-50/70 text-blue-900 font-bold' : 'text-slate-800'
                  }`}
                >
                  <span className="truncate">{o.label}</span>
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => {}}
                    className="h-3.5 w-3.5 accent-blue-600 rounded cursor-pointer shrink-0"
                  />
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

const ATTENTION_QUEUES: QueueKey[] = ['awaitingDecision', 'financeReview', 'redlistReview', 'disclosureReview', 'pastDeadline', 'stale', 'paused'];
const QUEUE_ICONS: Record<QueueKey, React.ReactNode> = {
  ready: <CheckCircle2 className="h-3.5 w-3.5" />,
  blocked: <Lock className="h-3.5 w-3.5" />,
  needsAttention: <AlertTriangle className="h-3.5 w-3.5" />,
  overridden: <ShieldCheck className="h-3.5 w-3.5" />,
  awaitingDecision: <Hand className="h-3.5 w-3.5" />,
  financeReview: <Clock3 className="h-3.5 w-3.5" />,
  redlistReview: <AlertTriangle className="h-3.5 w-3.5" />,
  disclosureReview: <AlertTriangle className="h-3.5 w-3.5" />,
  pastDeadline: <Clock3 className="h-3.5 w-3.5" />,
  stale: <Clock3 className="h-3.5 w-3.5" />,
  paused: <PauseCircle className="h-3.5 w-3.5" />,
};

/* ── main ────────────────────────────────────────────────────────────── */

export const WorkflowPage: React.FC<WorkflowPageProps> = ({ onViewApplicant }) => {
  const { programs } = usePrograms();

  const [summary, setSummary] = useState<WorkflowSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [staleSummary, setStaleSummary] = useState(false);
  const [lastOkAt, setLastOkAt] = useState<number | null>(null);

  const [records, setRecords] = useState<WorkflowRecord[]>([]);
  const [recordsLoading, setRecordsLoading] = useState(true);
  const [recordsError, setRecordsError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Ready lane (independent of list filters, honours program)
  const [rtmRecords, setRtmRecords] = useState<WorkflowRecord[]>([]);

  // Workflow settings panel
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Assign Automation Modal
  const [assignAutoOpen, setAssignAutoOpen] = useState(false);
  const [selectedApplicantForAuto, setSelectedApplicantForAuto] = useState<WorkflowRecord | null>(null);
  const [assignAutoApplicantIds, setAssignAutoApplicantIds] = useState<string[]>([]);

  // Filters
  const [programFilter, setProgramFilter] = useState('all');
  const [stageFilter, setStageFilter] = useState<string>('all');
  const [queueFilter, setQueueFilter] = useState<string>('all');
  const [batchesList, setBatchesList] = useState<Array<{ id: string; batchId: string; name: string; numericId?: number }>>([]);
  const [teamMembersList, setTeamMembersList] = useState<any[]>([]);
  const [selectedBatches, setSelectedBatches] = useState<string[]>([]);
  const [q, setQ] = useState('');
  const [qCommitted, setQCommitted] = useState('');
  const [sort, setSort] = useState('recent');

  useEffect(() => {
    safeFetch('/api/admin/batches')
      .then((data) => {
        const bList = Array.isArray(data?.batches) ? data.batches : Array.isArray(data) ? data : [];
        setBatchesList(bList);
      })
      .catch(() => setBatchesList([]));
  }, []);

  // Staff list for the unified bar's Assigned Team Member criterion
  // (same source CreateInternalTaskModal uses for assignees). A failed
  // load degrades to the bar's built-in 'Unassigned' option only.
  useEffect(() => {
    safeFetch('/api/admin/users')
      .then((data) => {
        const uList = Array.isArray(data?.users) ? data.users : Array.isArray(data) ? data : [];
        setTeamMembersList(uList);
      })
      .catch(() => setTeamMembersList([]));
  }, []);

  const batchOptions = useMemo(() => {
    return batchesList.map((b) => {
      const displayId = b.batchId?.startsWith('BAT-')
        ? b.batchId
        : b.numericId
        ? `BAT-${b.numericId}`
        : b.id || b.batchId || '—';
      return {
        value: b.batchId || b.id,
        label: `${displayId} - ${b.name}`,
      };
    });
  }, [batchesList]);

  const [unifiedFilters, setUnifiedFilters] = useState<ApplicantFilterState>(DEFAULT_FILTER_STATE);

  // Referrer options for the unified bar, derived from the records on
  // this page (the heavy /api/admin/referrers enrichment is not needed
  // to label a filter): one entry per referrerId actually present.
  const referrersList = useMemo(() => {
    const seen = new Map<string, any>();
    records.forEach((r) => {
      const rid = (r as any).referrerId;
      if (rid && !seen.has(rid)) seen.set(rid, { id: rid, referrerId: rid });
    });
    return Array.from(seen.values());
  }, [records]);

  // The engine only ever sees the criteria this screen can feed.
  const effectiveFilters = useMemo(
    () => excludeFilterCriteria(unifiedFilters, WORKFLOW_HIDDEN_CRITERIA),
    [unifiedFilters]
  );

  const filteredRecords = useMemo(() => {
    return records.filter((r) => filterApplicantRecord(r, effectiveFilters, { includeMasterPhase: false, batchesList, programsList: programs }));
  }, [records, effectiveFilters, batchesList, programs]);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Snapshot of every selected record, keyed by uid. The page's
  // `records` array is replaced whenever the admin pages, filters or
  // sorts, so resolving the selection through the current page (the
  // old recordByUid pattern) silently dropped applicants chosen on
  // other pages: the bulk-bar count and the assign payload disagreed.
  // Bulk actions resolve their targets from this snapshot instead, so
  // the count an admin sees is always the set that gets acted on.
  const [selectedRecords, setSelectedRecords] = useState<Map<string, WorkflowRecord>>(new Map());
  const clearSelection = useCallback(() => {
    setSelected(new Set());
    setSelectedRecords(new Map());
  }, []);
  const [drawerUid, setDrawerUid] = useState<string | null>(null);
  const [showBulkAdvance, setShowBulkAdvance] = useState(false);
  const [bulkUids, setBulkUids] = useState<string[]>([]);
  const [showBulkNudge, setShowBulkNudge] = useState(false);
  const [bulkPausing, setBulkPausing] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Mass Phase Change & Bulk Stage Nudge state
  const [showMassPhaseModal, setShowMassPhaseModal] = useState(false);
  const [massPhaseTarget, setMassPhaseTarget] = useState<StandardPhaseKey>('draft');
  const [massPhaseNote, setMassPhaseNote] = useState('');
  const [submittingMassPhase, setSubmittingMassPhase] = useState(false);
  const [triggeringBulkNudge, setTriggeringBulkNudge] = useState(false);

  const handleBulkTriggerStageNudge = async () => {
    if (selected.size === 0) return;
    setTriggeringBulkNudge(true);
    try {
      const res = await safeFetch('/api/admin/workflow/bulk-stage-nudge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ applicationIds: Array.from(selected) }),
      });
      if (res.success) {
        notifyToast('success', res.message || `Bulk Stage Nudge job queued for ${selected.size} applicants.`);
      } else {
        notifyToast('error', res.error || 'Failed to trigger bulk stage nudge');
      }
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to trigger bulk stage nudge');
    } finally {
      setTriggeringBulkNudge(false);
    }
  };

  const handleExecuteMassPhaseChange = async () => {
    if (selected.size === 0) return;
    if (!massPhaseNote.trim() || massPhaseNote.trim().length < 10) {
      notifyToast('warning', 'Please enter an admin note (at least 10 characters) explaining this bulk phase change.');
      return;
    }
    setSubmittingMassPhase(true);
    try {
      const res = await safeFetch('/api/admin/workflow/bulk-stage-move', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          applicationIds: Array.from(selected),
          targetStage: massPhaseTarget,
          adminNote: massPhaseNote.trim(),
        }),
      });
      if (res.success) {
        notifyToast('success', res.message || `Mass phase change queued for ${selected.size} applicants.`);
        setShowMassPhaseModal(false);
        setMassPhaseNote('');
        clearSelection();
        refreshAll();
      } else {
        notifyToast('error', res.error || 'Failed to queue mass phase change');
      }
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to queue mass phase change');
    } finally {
      setSubmittingMassPhase(false);
    }
  };

  // Bumps to refresh the Internal Tasks section after changes
  const [sectionsSignal, setSectionsSignal] = useState(0);
  // Pipeline vs Internal Tasks sub-view (decluttered 2026-10-11: tasks live on
  // their own designated view, not crowded onto the pipeline page)
  const [pageView, setPageView] = useState<'pipeline' | 'tasks' | 'redlist'>('pipeline');

  const searchRef = useRef<HTMLInputElement>(null);
  const animatedRtmIds = useRef<Set<string>>(new Set());

  const pageShare = useMemo(
    () => pageSelectionShare(records.map((r) => r.uid), [...selected]),
    [records, selected]
  );
  const pausedStages = useMemo(() => new Set((summary?.stages || []).filter((s) => s.paused).map((s) => s.key)), [summary]);

  /* ── data ─────────────────────────────────────────────────────── */

  const fetchSummary = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (programFilter !== 'all') params.set('program', programFilter);
      if (selectedBatches.length > 0) params.set('batches', selectedBatches.join(','));
      const data = await safeFetch(`/api/admin/workflow/v2/summary?${params.toString()}`);
      setSummary(data as WorkflowSummary);
      setSummaryError(null);
      setStaleSummary(false);
      setLastOkAt(Date.now());
    } catch (err: any) {
      if (summary) {
        setStaleSummary(true);
      } else {
        setSummaryError(err.message || 'Failed to load workflow summary');
      }
    } finally {
      setSummaryLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programFilter, selectedBatches]);

  // Response-sequence guards (PR-20): rapid filter/page changes race;
  // only the newest request may paint, so a slow earlier response can
  // never resurrect stale rows under newer filters.
  const recordsSeqRef = useRef(0);
  const rtmSeqRef = useRef(0);

  const fetchRecords = useCallback(async (opts?: { pageOverride?: number }) => {
    const seq = ++recordsSeqRef.current;
    try {
      setRecordsLoading(true);
      setRecordsError(null);
      const params = new URLSearchParams();
      if (stageFilter !== 'all') params.set('stage', stageFilter);
      if (queueFilter !== 'all') params.set('queue', queueFilter);
      if (programFilter !== 'all') params.set('program', programFilter);
      if (selectedBatches.length > 0) params.set('batches', selectedBatches.join(','));
      if (qCommitted.trim()) params.set('q', qCommitted.trim());
      params.set('sort', sort);
      params.set('page', String(opts?.pageOverride ?? page));
      params.set('pageSize', String(PAGE_SIZE));
      const data = await safeFetch(`/api/admin/workflow/v2/records?${params.toString()}`);
      if (seq !== recordsSeqRef.current) return;
      setRecords(dedupeWorkflowRows(data.records || []));
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
      setPage(data.page || 1);
    } catch (err: any) {
      if (seq !== recordsSeqRef.current) return;
      setRecordsError(err.message || 'Failed to load applicants');
    } finally {
      if (seq === recordsSeqRef.current) setRecordsLoading(false);
    }
  }, [stageFilter, queueFilter, programFilter, qCommitted, sort, page, selectedBatches]);

  const fetchRtmLane = useCallback(async () => {
    const seq = ++rtmSeqRef.current;
    try {
      const params = new URLSearchParams({ queue: 'ready', page: '1', pageSize: '24', sort: 'recent' });
      if (programFilter !== 'all') params.set('program', programFilter);
      if (selectedBatches.length > 0) params.set('batches', selectedBatches.join(','));
      const data = await safeFetch(`/api/admin/workflow/v2/records?${params.toString()}`);
      if (seq !== rtmSeqRef.current) return;
      setRtmRecords(dedupeWorkflowRows(data.records || []));
    } catch {
      if (seq === rtmSeqRef.current) setRtmRecords([]);
    }
  }, [programFilter, selectedBatches]);

  const refreshAll = useCallback(() => {
    fetchSummary();
    fetchRecords();
    fetchRtmLane();
  }, [fetchSummary, fetchRecords, fetchRtmLane]);

  useEffect(() => {
    fetchSummary();
    const interval = setInterval(fetchSummary, 60000);
    return () => clearInterval(interval);
  }, [fetchSummary]);

  useEffect(() => {
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchRecords]);

  // Freshness on return (PR-20): the summary already polls every 60s,
  // but the applicant LIST only refetched on filter/page changes — an
  // admin returning to the tab saw a fresh header over stale rows.
  // Refetch everything when the tab becomes visible / window regains
  // focus, throttled to once per 20s.
  const lastFocusRefreshRef = useRef(0);
  useEffect(() => {
    const maybeRefresh = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastFocusRefreshRef.current < 20000) return;
      lastFocusRefreshRef.current = Date.now();
      refreshAll();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') maybeRefresh();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', maybeRefresh);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', maybeRefresh);
    };
  }, [refreshAll]);

  useEffect(() => {
    if (summary && summary.totals.ready > 0) fetchRtmLane();
    else setRtmRecords([]);
  }, [summary, fetchRtmLane]);

  // Debounce search → commit → reset to page 1
  useEffect(() => {
    const t = setTimeout(() => {
      setQCommitted((prev) => {
        if (prev !== q) setPage(1);
        return q;
      });
    }, 350);
    return () => clearTimeout(t);
  }, [q]);

  // "/" focuses search anywhere
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/') return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /* ── actions ──────────────────────────────────────────────────── */

  const copyApplicationId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch { /* clipboard unavailable */ }
  };

  const handleAdvanceOne = async (uid: string) => {
    try {
      await safeFetch(`/api/admin/workflow/applicants/${encodeURIComponent(uid)}/advance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      notifyToast('success', 'Applicant advanced');
      refreshAll();
    } catch (err: any) {
      notifyToast('error', err.message || 'Advance failed');
    }
  };

  const handleResumeEmails = async () => {
    try {
      await safeFetch('/api/admin/workflow/pause', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ level: 'global', paused: false }),
      });
      notifyToast('success', 'Nudge emails resumed');
      fetchSummary();
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to resume emails');
    }
  };

  const handleBulkPause = async (paused: boolean) => {
    const uids = [...selected];
    if (!uids.length) return;
    try {
      setBulkPausing(true);
      await Promise.all(
        uids.map((uid) =>
          safeFetch('/api/admin/workflow/pause', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ level: 'application', uid, paused }),
          })
        )
      );
      notifyToast('success', `Emails ${paused ? 'paused' : 'resumed'} for ${uids.length} applicant${uids.length === 1 ? '' : 's'}`);
      clearSelection();
      refreshAll();
    } catch (err: any) {
      notifyToast('error', err.message || 'Bulk pause failed');
    } finally {
      setBulkPausing(false);
    }
  };

  const handleExportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (stageFilter !== 'all') params.set('stage', stageFilter);
      if (queueFilter !== 'all') params.set('queue', queueFilter);
      if (programFilter !== 'all') params.set('program', programFilter);
      if (selectedBatches.length > 0) params.set('batches', selectedBatches.join(','));
      if (qCommitted.trim()) params.set('q', qCommitted.trim());
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`/api/admin/workflow/export?${params.toString()}`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...workspaceHeader(),
        },
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `Export failed (HTTP ${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `workflow_export_${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      notifyToast('success', 'CSV export downloaded');
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to export CSV');
    }
  };

  const toggleSelect = (record: WorkflowRecord) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(record.uid)) next.delete(record.uid);
      else next.add(record.uid);
      return next;
    });
    setSelectedRecords((prev) => {
      const next = new Map(prev);
      if (next.has(record.uid)) next.delete(record.uid);
      else next.set(record.uid, record);
      return next;
    });
  };

  // Page-scoped header toggle, on the shared batchSelection semantics:
  // a fully selected page loses exactly its own ids (selections from
  // other pages survive); anything less selects the whole page.
  const toggleSelectAll = () => {
    const pageUids = records.map((r) => r.uid);
    const share = pageSelectionShare(pageUids, [...selected]);
    if (share === 'all') {
      setSelected((prev) => {
        const next = new Set(prev);
        pageUids.forEach((u) => next.delete(u));
        return next;
      });
      setSelectedRecords((prev) => {
        const next = new Map(prev);
        pageUids.forEach((u) => next.delete(u));
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        pageUids.forEach((u) => next.add(u));
        return next;
      });
      setSelectedRecords((prev) => {
        const next = new Map(prev);
        records.forEach((r) => next.set(r.uid, r));
        return next;
      });
    }
  };

  const clearFilters = () => {
    setStageFilter('all');
    setQueueFilter('all');
    setProgramFilter('all');
    setSelectedBatches([]);
    setQ('');
    setPage(1);
  };

  const hasFilters = stageFilter !== 'all' || queueFilter !== 'all' || programFilter !== 'all' || selectedBatches.length > 0 || q.trim() !== '';

  /* ── derived ──────────────────────────────────────────────────── */

  const totals = summary?.totals || { active: 0, ready: 0, blocked: 0, needsAttention: 0, overridden: 0 };
  const automation = summary?.automation;
  const queues = summary?.queues;
  const stages = summary?.stages || [];

  const kpiTiles: { key: string; label: string; value: number; queue: string | null; tone: string; icon: React.ReactNode }[] = [
    { key: 'active', label: 'Total active', value: totals.active, queue: null, tone: 'bg-white', icon: null },
    { key: 'ready', label: 'Ready to Matriculate', value: totals.ready, queue: 'ready', tone: 'bg-emerald-50/60', icon: <CheckCircle2 className="h-4 w-4 text-emerald-600" /> },
    { key: 'blocked', label: 'Blocked', value: totals.blocked, queue: 'blocked', tone: 'bg-amber-50/60', icon: <Lock className="h-4 w-4 text-amber-600" /> },
    { key: 'needsAttention', label: 'Needs attention', value: totals.needsAttention, queue: 'needsAttention', tone: 'bg-red-50/60', icon: <AlertTriangle className="h-4 w-4 text-red-600" /> },
    { key: 'overridden', label: 'Overridden', value: totals.overridden, queue: 'overridden', tone: 'bg-[var(--color-override-bg)]', icon: <ShieldCheck className="h-4 w-4 text-[var(--color-override)]" /> },
  ];

  const drawerUids = records.map((r) => r.uid);

  /* ── render ───────────────────────────────────────────────────── */

  if (summaryLoading && !summary) {
    return (
      <div className="h-full w-full flex flex-col items-center justify-center p-8 space-y-4 font-sans">
        <div className="premium-loader" />
        <p className="text-xs font-bold text-slate-500">Loading Workflow…</p>
      </div>
    );
  }

  if (summaryError && !summary) {
    return (
      <div className="h-full w-full flex flex-col items-center justify-center p-8 space-y-3 font-sans">
        <AlertTriangle className="h-8 w-8 text-red-500" />
        <p className="text-sm font-bold text-slate-700">Couldn't load the workflow board</p>
        <p className="text-xs text-slate-500 max-w-sm text-center">{summaryError}</p>
        <button type="button" onClick={fetchSummary} className="px-4 py-2.5 min-h-[44px] rounded-xl bg-brand hover:bg-brand-active text-white text-xs font-bold cursor-pointer flex items-center gap-1.5">
          <RefreshCw className="h-4 w-4" /> Retry
        </button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col p-4 sm:p-6 space-y-4 sm:space-y-5 overflow-y-auto font-sans bg-slate-50/50">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 shrink-0">
        <div>
          <h1 className="text-xl font-extrabold text-slate-900 tracking-tight">Workflow</h1>
          <p className="text-xs font-semibold text-slate-500 mt-0.5">Every applicant, every stage, every blocker.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-52">
            <SearchableSelect
              value={programFilter === 'all' ? '' : programFilter}
              onChange={(v) => { setProgramFilter(v || 'all'); setPage(1); }}
              ariaLabel="Filter by program"
              options={programs.map((p) => ({
                value: p.name,
                label: p.code ? `${p.code} — ${p.name}` : p.name,
              }))}
              placeholder="Search programs…"
              clearLabel="All Programs"
              emptyLabel="No programs found"
            />
          </div>
          <div className="w-60">
            <SearchableMultiSelect
              values={selectedBatches}
              onChange={(v) => { setSelectedBatches(v); setPage(1); }}
              options={batchOptions}
              placeholder="Filter by Batches…"
              emptyLabel="No batches found"
              ariaLabel="Filter by batches"
            />
          </div>
          <div className="relative flex-1 sm:flex-none sm:w-64">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              ref={searchRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, ID…  ( / )"
              aria-label="Search applicants"
              className="w-full bg-white border border-slate-200/80 rounded-xl pl-9 pr-3 py-2.5 min-h-[44px] text-xs font-medium text-slate-800 placeholder:text-slate-400 shadow-2xs outline-none focus:border-blue-400"
            />
          </div>
          <span className="text-[11px] font-semibold text-slate-500 whitespace-nowrap">
            Last updated {lastOkAt ? fmtTime(new Date(lastOkAt).toISOString()) : '—'}
          </span>
          <button
            type="button"
            onClick={refreshAll}
            aria-label="Refresh workflow data"
            className="p-2.5 min-w-[44px] min-h-[44px] bg-white border border-slate-200/80 rounded-xl text-slate-600 hover:bg-slate-50 shadow-2xs flex items-center justify-center cursor-pointer"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => {
              // The toolbar must NEVER silently target records[0] — the
              // first row on the page is not a chosen applicant, and an
              // automation assigned to the wrong person sends real
              // emails and finance records. The target is the admin's
              // explicit checkbox selection (one or many); with no
              // selection the button guides instead of guessing.
              const chosen = [...selected]
                .map((uid) => selectedRecords.get(uid))
                .filter((r): r is WorkflowRecord => !!r);
              if (chosen.length === 0) {
                notifyToast(
                  'info',
                  'Select at least one applicant in the list to assign an automation — or open an applicant and use Assign Flow in their drawer.'
                );
                return;
              }
              setSelectedApplicantForAuto(chosen[0]);
              setAssignAutoApplicantIds(
                chosen.map((r) => r.applicationId || (r as any).id || r.uid)
              );
              setAssignAutoOpen(true);
            }}
            className="flex items-center gap-1.5 px-3 py-2.5 min-h-[44px] bg-brand-primary text-white rounded-xl text-xs font-extrabold hover:bg-brand-primary-dark shadow-2xs cursor-pointer"
          >
            <Sparkles className="h-4 w-4" />
            Assign Automation
          </button>
          <button
            type="button"
            onClick={() => setSettingsOpen(true)}
            aria-label="Workflow settings"
            className="p-2.5 min-w-[44px] min-h-[44px] bg-white border border-slate-200/80 rounded-xl text-slate-600 hover:bg-slate-50 shadow-2xs flex items-center justify-center cursor-pointer"
          >
            <Settings className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Sub-view tabs: clean pipeline vs designated Internal Tasks page vs Redlist */}
      <div className="shrink-0 flex items-center gap-1 bg-white border border-slate-200/80 rounded-xl p-1 shadow-2xs w-fit">
        {(['pipeline', 'tasks'] as const).map((v) => (
          <button
            key={v}
            type="button"
            aria-pressed={pageView === v}
            onClick={() => setPageView(v)}
            className={`px-4 py-2 rounded-lg text-xs font-extrabold cursor-pointer transition-colors ${
              pageView === v ? 'bg-brand text-white shadow-xs' : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            {v === 'pipeline' ? 'Pipeline' : 'Internal Tasks'}
          </button>
        ))}
        {/* Redlist — discrete: same tab row, red accent so it's noticeable without shouting */}
        <button
          type="button"
          aria-pressed={pageView === 'redlist'}
          onClick={() => setPageView('redlist')}
          title="Manage redlisted contacts"
          className={`px-4 py-2 rounded-lg text-xs font-extrabold cursor-pointer transition-colors flex items-center gap-1.5 ${
            pageView === 'redlist'
              ? 'bg-red-700 text-white shadow-xs'
              : 'text-red-700/80 hover:text-red-800 hover:bg-red-50'
          }`}
        >
          <ShieldAlert className="h-3.5 w-3.5" />
          Redlist
        </button>
      </div>

      {/* Stale banner */}
      {staleSummary && lastOkAt && (
        <div className="bg-slate-100 border border-slate-200 rounded-xl px-4 py-2.5 text-xs font-semibold text-slate-600 flex items-center gap-2">
          <Clock3 className="h-4 w-4" />
          Showing data from {Math.max(1, Math.round((Date.now() - lastOkAt) / 60000))} min ago — refresh failed, retrying.
          <button type="button" onClick={refreshAll} className="ml-auto underline font-bold cursor-pointer">Retry now</button>
        </div>
      )}

      {/* Automation strip */}
      {automation && (
        <div className="space-y-2 shrink-0">
          {automation.emailsPaused && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex flex-wrap items-center gap-2">
              <Mail className="h-4 w-4 text-amber-700 shrink-0" />
              <p className="text-xs font-bold text-amber-900">
                Nudge emails are paused{automation.emailsPausedBy ? ` — by ${automation.emailsPausedBy}` : ''}{automation.emailsPausedAt ? ` at ${fmtDateTime(automation.emailsPausedAt)}` : ''}
              </p>
              <button type="button" onClick={handleResumeEmails} className="ml-auto px-3 py-2 min-h-[44px] rounded-xl bg-amber-600 hover:bg-amber-500 text-white text-xs font-bold flex items-center gap-1.5 cursor-pointer">
                <Play className="h-3.5 w-3.5" /> Resume
              </button>
            </div>
          )}
          {!automation.autoAdvanceEnabled && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-2">
              <PauseCircle className="h-4 w-4 text-red-600 shrink-0" />
              <p className="text-xs font-bold text-red-800">Auto-advance is paused/disabled — eligible applicants will not move automatically.</p>
            </div>
          )}
          <p className="text-[11px] font-medium text-slate-500 px-1">
            Next auto-advance: <span className="font-bold text-slate-700">{relativeTime(automation.nextRunAt)}</span>
            {' · '}Last run: <span className="font-bold text-slate-700">{automation.lastRun ? `${automation.lastRun.advanced} advanced, ${automation.lastRun.skipped} skipped (${relativeTime(automation.lastRun.at)})` : '—'}</span>
            {' · '}Next email send: <span className="font-bold text-slate-700">—</span>
          </p>
        </div>
      )}

      {pageView === 'pipeline' && (
      <>
      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 shrink-0">
        {kpiTiles.map((t) => {
          const active = t.queue === null ? queueFilter === 'all' : queueFilter === t.queue;
          return (
            <button
              key={t.key}
              type="button"
              aria-pressed={active}
              onClick={() => { setQueueFilter(t.queue ?? 'all'); setPage(1); }}
              className={`${t.tone} border rounded-2xl px-4 py-3 text-left shadow-2xs cursor-pointer transition-all ${active ? 'border-blue-500 ring-2 ring-blue-100' : 'border-slate-200/80 hover:border-slate-300'}`}
            >
              <div className="flex items-center gap-1.5">
                {t.icon}
                <span className="text-[10px] font-extrabold uppercase tracking-wide text-slate-500">{t.label}</span>
              </div>
              <p className="text-2xl font-extrabold text-slate-900 mt-1 tabular-nums">{t.value}</p>
            </button>
          );
        })}
      </div>

      {/* Pipeline rail */}
      <div className="shrink-0">
        <div className="flex items-center gap-2 mb-2 px-1">
          <h2 className="text-xs font-extrabold uppercase tracking-wide text-slate-500">Pipeline</h2>
          {summary?.unmapped ? (
            <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
              {summary.unmapped} unmapped
            </span>
          ) : null}
        </div>
        {/* At-a-glance stage mix (PR-20): one proportional bar — where the
            whole caseload sits, before reading a single card. Segments are
            exactly the summary's stage entries (see pipelineSegments.ts):
            the 'closed' stage count already covers every closed record, so
            no extra Closure segment is appended — the SD/SC/SP breakdown
            on the Closure card below is a subset view, not an addition. */}
        {(() => {
          const { segments: segs, total: totalAll } = buildPipelineSegments(stages);
          if (totalAll <= 0) return null;
          return (
            <div
              className="flex h-2 rounded-full overflow-hidden bg-slate-100 mb-3 mx-1"
              role="img"
              aria-label={`Pipeline distribution: ${segs.map((s) => `${s.label} ${s.count}`).join(', ')}`}
            >
              {segs.map((s) => (
                <div key={s.key} className={s.tone} style={{ width: `${(s.count / totalAll) * 100}%` }} title={`${s.label}: ${s.count}`} />
              ))}
            </div>
          );
        })()}
        <div className="flex gap-2 sm:gap-3 overflow-x-auto pb-2">
          {stages.map((s, i) => {
            const selected = stageFilter === s.key;
            const maxSub = Math.max(1, ...s.topSubStatuses.map((t) => t.count));
            return (
              <button
                key={s.key}
                type="button"
                aria-pressed={selected}
                onClick={() => { setStageFilter(selected ? 'all' : s.key); setPage(1); }}
                className={`min-w-[190px] max-w-[220px] flex-1 bg-white border rounded-2xl p-3.5 text-left shadow-2xs cursor-pointer transition-all ${selected ? 'border-blue-500 ring-2 ring-blue-100' : 'border-slate-200/80 hover:border-slate-300'}`}
              >
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] font-extrabold text-slate-400 tabular-nums">{String(i + 1).padStart(2, '0')}</span>
                  {s.paused && (
                    <span title="Stage paused" aria-label="Stage paused">
                      <PauseCircle className="h-3.5 w-3.5 text-slate-400" />
                    </span>
                  )}
                  <span className="text-[11px] font-extrabold text-slate-800 leading-tight">{s.label}</span>
                </div>
                <p className="text-3xl font-extrabold text-slate-900 mt-1 tabular-nums">{s.count}</p>
                <div className="flex items-center gap-2 mt-1.5 text-[10px] font-bold">
                  <span className="flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-3 w-3" />{s.ready}</span>
                  <span className="flex items-center gap-1 text-amber-700"><Lock className="h-3 w-3" />{s.blocked}</span>
                  <span className="flex items-center gap-1 text-red-700"><Clock3 className="h-3 w-3" />{s.stale}</span>
                </div>
                {s.topSubStatuses.length > 0 && (
                  <div className="mt-2.5 space-y-1.5">
                    {s.topSubStatuses.slice(0, 3).map((t) => (
                      <div key={t.label}>
                        <div className="flex items-center justify-between text-[10px] font-semibold text-slate-600">
                          <span className="truncate mr-1">{t.label}</span>
                          <span className="tabular-nums">{t.count}</span>
                        </div>
                        <div className="h-1 bg-slate-100 rounded-full mt-0.5">
                          <div className="h-1 bg-brand-primary rounded-full" style={{ width: `${Math.round((t.count / maxSub) * 100)}%` }} />
                        </div>
                      </div>
                    ))}
                    {s.subStatusCount > 3 && (
                      <p className="text-[10px] font-bold text-blue-600">View all {s.subStatusCount}</p>
                    )}
                  </div>
                )}
              </button>
            );
          })}
          {/* Closure card */}
          <div className="min-w-[190px] max-w-[220px] flex-1 bg-white border border-slate-200/80 rounded-2xl p-3.5 shadow-2xs">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-extrabold text-slate-400 tabular-nums">08</span>
              <span className="text-[11px] font-extrabold text-slate-800">Closure</span>
            </div>
            <div className="mt-2 space-y-1.5">
              {(['SD', 'SC', 'SP'] as const).map((k) => (
                <div key={k} className="flex items-center justify-between">
                  <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-600">{k}</span>
                  <span className="text-sm font-extrabold text-slate-900 tabular-nums">{summary?.closure?.[k] ?? 0}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Ready to Matriculate lane */}
      {totals.ready > 0 && (
        <div className="shrink-0 bg-emerald-50/50 border border-emerald-200/70 rounded-2xl p-3.5" aria-live="polite">
          <div className="flex items-center gap-2 mb-2.5 px-1">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            <h2 className="text-xs font-extrabold text-emerald-900">
              Ready to Matriculate — {totals.ready} applicant{totals.ready === 1 ? '' : 's'}
            </h2>
            <button
              type="button"
              onClick={() => { setBulkUids(rtmRecords.map((r) => r.uid)); setShowBulkAdvance(true); }}
              className="ml-auto text-[11px] font-bold text-emerald-700 underline hover:text-emerald-800 cursor-pointer"
            >
              Advance all visible
            </button>
          </div>
          <div className="flex gap-2.5 overflow-x-auto pb-1">
            {rtmRecords.map((r) => {
              const idx = STAGE_ORDER.indexOf(r.stage as any);
              const next = idx >= 0 && idx < STAGE_ORDER.length - 1 ? STAGE_LABELS[STAGE_ORDER[idx + 1]] : '—';
              const cur = STAGE_LABELS[r.stage as keyof typeof STAGE_LABELS] || r.stage;
              return (
                <div
                  key={r.uid}
                  onClick={() => setDrawerUid(r.uid)}
                  title={`Open ${r.displayName}'s drawer`}
                  className="min-w-[240px] bg-white border border-emerald-200/80 rounded-2xl p-3 shadow-2xs flex items-center gap-2.5 cursor-pointer hover:border-emerald-300"
                >
                  <ReadyCheck uid={r.uid} animatedIds={animatedRtmIds} />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-extrabold text-slate-900 truncate">{r.displayName}</p>
                    <p className="font-mono text-[11px] text-slate-500">#{r.applicationId}</p>
                    <p className="text-[10px] font-semibold text-slate-500 truncate">{cur} → {next}</p>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleAdvanceOne(r.uid); }}
                    aria-label={`Advance ${r.displayName} now`}
                    className="shrink-0 px-3 py-2 min-h-[44px] rounded-xl bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] text-white text-[11px] font-bold cursor-pointer"
                  >
                    Advance now
                  </button>
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-emerald-700/80 font-medium mt-2 px-1">These will advance automatically in the next 24-hour run.</p>
        </div>
      )}

      {/* Attention queues */}
      {queues && (
        <div className="shrink-0">
          <h2 className="text-xs font-extrabold uppercase tracking-wide text-slate-500 mb-2 px-1">Attention queues</h2>
          <div className="flex flex-wrap gap-2">
            {ATTENTION_QUEUES.map((k) => {
              const count = (queues as Record<string, number>)[k] ?? 0;
              const active = queueFilter === k;
              return (
                <button
                  key={k}
                  type="button"
                  aria-pressed={active}
                  onClick={() => { setQueueFilter(active ? 'all' : k); setPage(1); }}
                  className={`flex items-center gap-1.5 px-3 py-2 min-h-[44px] rounded-full border text-xs font-bold shadow-2xs cursor-pointer transition-all ${active ? 'bg-brand text-white border-brand' : 'bg-white text-slate-700 border-slate-200/80 hover:border-slate-300'}`}
                >
                  {QUEUE_ICONS[k]}
                  {QUEUE_LABELS[k]}
                  <span className={`tabular-nums px-1.5 py-0.5 rounded-full text-[10px] ${active ? 'bg-white/20' : 'bg-slate-100 text-slate-600'}`}>{count}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Active filter chips */}
      {hasFilters && (
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Filters:</span>
          {programFilter !== 'all' && (
            <button type="button" onClick={() => { setProgramFilter('all'); setPage(1); }} className="flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-blue-800 text-[11px] font-bold cursor-pointer">
              Program: {programFilter} <X className="h-3 w-3" />
            </button>
          )}
          {selectedBatches.map((bId) => {
            const opt = batchOptions.find((o) => o.value === bId);
            return (
              <button
                key={bId}
                type="button"
                onClick={() => {
                  setSelectedBatches(selectedBatches.filter((v) => v !== bId));
                  setPage(1);
                }}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-blue-800 text-[11px] font-bold cursor-pointer"
              >
                Batch: {opt ? opt.label : bId} <X className="h-3 w-3" />
              </button>
            );
          })}
          {stageFilter !== 'all' && (
            <button type="button" onClick={() => { setStageFilter('all'); setPage(1); }} className="flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-blue-800 text-[11px] font-bold cursor-pointer">
              Stage: {STAGE_LABELS[stageFilter as keyof typeof STAGE_LABELS] || stageFilter} <X className="h-3 w-3" />
            </button>
          )}
          {queueFilter !== 'all' && (
            <button type="button" onClick={() => { setQueueFilter('all'); setPage(1); }} className="flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-blue-800 text-[11px] font-bold cursor-pointer">
              Queue: {QUEUE_LABELS[queueFilter as QueueKey] || queueFilter} <X className="h-3 w-3" />
            </button>
          )}
          {q.trim() !== '' && (
            <button type="button" onClick={() => setQ('')} className="flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-blue-800 text-[11px] font-bold cursor-pointer">
              Search: “{q.trim()}” <X className="h-3 w-3" />
            </button>
          )}
          <button type="button" onClick={clearFilters} className="text-[11px] font-bold text-slate-500 underline cursor-pointer">Clear filters</button>
        </div>
      )}

      {/* Applicant list */}
      <div aria-busy={recordsLoading} className="bg-white border border-slate-200/80 rounded-2xl shadow-2xs overflow-hidden shrink-0 grow min-h-0 flex flex-col">
        <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-slate-100">
          <h2 className="text-sm font-extrabold text-slate-900 tracking-tight">
            Applicants <span className="text-slate-400 font-bold tabular-nums">({hasActiveFilters(effectiveFilters) ? filteredRecords.length : total})</span>
          </h2>
          <div className="ml-auto flex items-center gap-2">
            <label className="text-[11px] font-bold text-slate-500">
              Sort{' '}
              <select
                value={sort}
                onChange={(e) => { setSort(e.target.value); setPage(1); }}
                className="bg-white border border-slate-200/80 rounded-xl px-2 py-2 text-xs font-bold text-slate-700 cursor-pointer"
              >
                <option value="recent">Recent activity</option>
                <option value="name">Name</option>
                <option value="daysInStatus">Days in status</option>
                <option value="stage">Stage</option>
              </select>
            </label>
          </div>
        </div>

        {/* Unified Filters Bar above applicant table */}
        <div className="p-3 border-b border-slate-100 bg-slate-50/50">
          <UnifiedApplicantFilterBar
            filters={unifiedFilters}
            onFilterChange={setUnifiedFilters}
            includeMasterPhase={false}
            hiddenCriteria={WORKFLOW_HIDDEN_CRITERIA}
            programsList={programs}
            batchesList={batchesList}
            referrersList={referrersList}
            teamMembersList={teamMembersList}
            applications={records}
          />
        </div>

        {/* Background-refresh indicator (PR-20): rows stay mounted and
            usable while a refetch is in flight — never a blank flash. */}
        {recordsLoading && records.length > 0 && (
          <div className="h-0.5 bg-blue-100 shrink-0" role="progressbar" aria-label="Refreshing applicants">
            <div className="h-full w-full bg-brand-primary animate-pulse" />
          </div>
        )}

        <div className="overflow-auto grow">
          {recordsLoading && records.length === 0 ? (
            <div className="p-4 space-y-2">
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : recordsError && records.length === 0 ? (
            <div className="p-10 text-center space-y-3">
              <AlertTriangle className="h-8 w-8 text-red-500 mx-auto" />
              <p className="text-sm font-bold text-slate-700">Couldn't load applicants</p>
              <p className="text-xs text-slate-500 max-w-sm mx-auto">{recordsError}</p>
              <button type="button" onClick={() => fetchRecords()} className="px-4 py-2.5 min-h-[44px] rounded-xl bg-brand hover:bg-brand-active text-white text-xs font-bold cursor-pointer inline-flex items-center gap-1.5">
                <RefreshCw className="h-4 w-4" /> Retry
              </button>
            </div>
          ) : records.length === 0 ? (
            <div className="p-10 text-center space-y-2">
              <Search className="h-8 w-8 text-slate-300 mx-auto" />
              <p className="text-sm font-bold text-slate-700">No applicants match</p>
              <p className="text-xs text-slate-500">Try widening your filters or search.</p>
              {hasFilters && (
                <button type="button" onClick={clearFilters} className="mt-2 px-4 py-2.5 min-h-[44px] rounded-xl bg-blue-600 text-white text-xs font-bold cursor-pointer">
                  Clear all filters
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <table className="hidden md:table w-full text-left min-w-[1100px]">
                <thead className="sticky top-0 bg-slate-50/95 backdrop-blur z-10">
                  <tr className="text-[10px] font-extrabold uppercase tracking-wide text-slate-500">
                    <th className="px-4 py-2.5 w-10">
                      <input
                        type="checkbox"
                        aria-label="Select all on this page"
                        ref={(el) => { if (el) el.indeterminate = pageShare === 'some'; }}
                        checked={pageShare === 'all'}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 accent-blue-600 cursor-pointer"
                      />
                    </th>
                    <th className="px-3 py-2.5">Applicant</th>
                    <th className="px-3 py-2.5">Program / Term</th>
                    <th className="px-3 py-2.5">Stage / Sub-status</th>
                    <th className="px-3 py-2.5">Status</th>
                    <th className="px-3 py-2.5">Days in status</th>
                    <th className="px-3 py-2.5">Owner · Top blocker</th>
                    <th className="px-3 py-2.5">Placement / Admission</th>
                    <th className="px-3 py-2.5">Last activity</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map((r) => (
                    <tr
                      key={r.uid}
                      onClick={() => setDrawerUid(r.uid)}
                      className="border-t border-slate-100 hover:bg-slate-50/70 cursor-pointer"
                    >
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label={`Select ${r.displayName}`}
                          checked={selected.has(r.uid)}
                          onChange={() => toggleSelect(r)}
                          className="h-4 w-4 accent-blue-600 cursor-pointer"
                        />
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2.5">
                          <span className="h-8 w-8 rounded-xl bg-brand text-white flex items-center justify-center text-[10px] font-extrabold shrink-0" aria-hidden="true">
                            {initials(r.displayName)}
                          </span>
                          <div className="min-w-0">
                            <p className="text-xs font-extrabold text-slate-900 truncate">
                              {r.displayName}
                              {r.unclaimed && (
                                <span className="ml-1.5 align-middle text-[11px] font-extrabold uppercase tracking-wide bg-sky-50 text-sky-700 border border-sky-200 rounded-full px-1.5 py-px">
                                  Awaiting signup
                                </span>
                              )}
                            </p>
                            <p className="text-[11px] text-slate-500 truncate">{r.email}</p>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); copyApplicationId(r.applicationId); }}
                              title="Copy application ID"
                              className="font-mono text-[11px] text-slate-500 hover:text-slate-800 flex items-center gap-1 cursor-pointer"
                            >
                              #{r.applicationId}
                              {copiedId === r.applicationId ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
                            </button>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <p className="text-xs font-bold text-slate-800">{r.program}</p>
                        <p className="text-[11px] text-slate-500">{r.season || '—'}</p>
                      </td>
                      <td className="px-3 py-3">
                        <p className="text-xs font-bold text-slate-800">{STAGE_LABELS[r.stage as keyof typeof STAGE_LABELS] || r.stage}</p>
                        <p className="text-[11px] text-slate-500">{r.subStatus || '—'}</p>
                      </td>
                      <td className="px-3 py-3">
                        <StatusSignal record={r} pausedStages={pausedStages} stage={r.stage} />
                      </td>
                      <td className="px-3 py-3">
                        <span className={`text-xs font-bold tabular-nums ${r.isStale ? 'text-red-600' : 'text-slate-700'}`}>
                          {r.daysInStatus}d
                        </span>
                        {r.isStale && <span className="block text-[10px] font-bold text-red-500">stale</span>}
                        {r.isPastTarget && <span className="block text-[10px] font-bold text-amber-600">past target</span>}
                      </td>
                      <td className="px-3 py-3 max-w-[220px]">
                        <p className="text-xs font-bold text-slate-700">{r.ownerName || <span className="text-slate-400 font-medium">Unassigned</span>}</p>
                        {r.topBlocker && (
                          <p className="text-[11px] text-slate-500 truncate" title={r.topBlocker.message}>{r.topBlocker.message}</p>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <p className="text-xs font-medium text-slate-600">{r.confirmationType === 'none' ? 'n/a' : (r.confirmationLabel || '—')}</p>
                      </td>
                      <td className="px-3 py-3">
                        <p className="text-[11px] font-medium text-slate-500 whitespace-nowrap">{r.lastActivityAt ? relativeTime(r.lastActivityAt) : '—'}</p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-slate-100">
                {filteredRecords.map((r) => (
                  <div key={r.uid} className="p-4 flex gap-3" onClick={() => setDrawerUid(r.uid)}>
                    <input
                      type="checkbox"
                      aria-label={`Select ${r.displayName}`}
                      checked={selected.has(r.uid)}
                      onChange={(e) => { e.stopPropagation(); toggleSelect(r); }}
                      onClick={(e) => e.stopPropagation()}
                      className="h-5 w-5 accent-blue-600 cursor-pointer shrink-0 mt-1 min-h-[44px]"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="h-9 w-9 rounded-xl bg-brand text-white flex items-center justify-center text-[11px] font-extrabold shrink-0" aria-hidden="true">
                          {initials(r.displayName)}
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-extrabold text-slate-900 truncate">
                            {r.displayName}
                            {r.unclaimed && (
                              <span className="ml-1.5 align-middle text-[11px] font-extrabold uppercase tracking-wide bg-sky-50 text-sky-700 border border-sky-200 rounded-full px-1.5 py-px">
                                Awaiting signup
                              </span>
                            )}
                          </p>
                          <p className="font-mono text-[11px] text-slate-500">#{r.applicationId}</p>
                        </div>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <StatusSignal record={r} pausedStages={pausedStages} stage={r.stage} compact />
                        <span className="text-[11px] font-semibold text-slate-500">{STAGE_LABELS[r.stage as keyof typeof STAGE_LABELS] || r.stage}</span>
                      </div>
                      <p className="text-[11px] text-slate-500 mt-1 truncate">{r.program}{r.season ? ` · ${r.season}` : ''}</p>
                      {r.topBlocker && (
                        <p className="text-[11px] text-amber-700 mt-1 truncate">Blocked: {r.topBlocker.message}</p>
                      )}
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-300 shrink-0 mt-1" />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Pagination */}
        {records.length > 0 && (
          <div className="px-4 py-3 border-t border-slate-100 flex items-center gap-3">
            <p className="text-[11px] font-bold text-slate-500 tabular-nums">
              Page {page} of {totalPages} · {total} applicant{total === 1 ? '' : 's'}
            </p>
            <div className="ml-auto flex items-center gap-1">
              <button
                type="button"
                aria-label="Previous page"
                disabled={page <= 1 || recordsLoading}
                onClick={() => fetchRecords({ pageOverride: page - 1 })}
                className="p-2 min-w-[44px] min-h-[44px] rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 flex items-center justify-center cursor-pointer"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                aria-label="Next page"
                disabled={page >= totalPages || recordsLoading}
                onClick={() => fetchRecords({ pageOverride: page + 1 })}
                className="p-2 min-w-[44px] min-h-[44px] rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 flex items-center justify-center cursor-pointer"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Sticky bulk bar */}
      {selected.size > 0 && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[8800] bg-white text-slate-800 rounded-2xl shadow-2xl px-4 py-3 flex flex-wrap items-center gap-2 border border-slate-200 max-w-[95vw]">
          <span className="text-xs font-black px-2.5 py-1 bg-blue-600 rounded-lg whitespace-nowrap tabular-nums">
            {selected.size} selected
          </span>
          <button
            type="button"
            onClick={() => {
              const chosen = [...selected]
                .map((uid) => selectedRecords.get(uid))
                .filter((r): r is WorkflowRecord => !!r);
              setSelectedApplicantForAuto(chosen[0] || null);
              setAssignAutoApplicantIds(
                chosen.map((r) => r.applicationId || (r as any).id || r.uid)
              );
              setAssignAutoOpen(true);
            }}
            className="px-3 py-2 min-h-[44px] bg-brand-primary hover:bg-brand-primary-dark rounded-xl text-xs font-bold flex items-center gap-1.5 cursor-pointer"
          >
            <Sparkles className="h-3.5 w-3.5" /> Assign Automation
          </button>
          <button
            type="button"
            onClick={handleBulkTriggerStageNudge}
            disabled={triggeringBulkNudge}
            className="px-3 py-2 min-h-[44px] bg-brand-primary hover:bg-brand-primary-dark disabled:opacity-50 rounded-xl text-xs font-bold flex items-center gap-1.5 cursor-pointer"
          >
            <Send className="h-3.5 w-3.5" /> Trigger Stage Nudge
          </button>
          <button
            type="button"
            onClick={() => { setMassPhaseNote(''); setShowMassPhaseModal(true); }}
            className="px-3 py-2 min-h-[44px] bg-amber-600 hover:bg-amber-500 rounded-xl text-xs font-bold flex items-center gap-1.5 cursor-pointer"
          >
            <ArrowRightLeft className="h-3.5 w-3.5" /> Mass Phase Change
          </button>
          <button
            type="button"
            onClick={() => setShowBulkNudge(true)}
            className="px-3 py-2 min-h-[44px] bg-brand-primary hover:bg-brand-primary-dark rounded-xl text-xs font-bold flex items-center gap-1.5 cursor-pointer"
          >
            <Mail className="h-3.5 w-3.5" /> Send status email now
          </button>
          <button
            type="button"
            onClick={() => { setBulkUids([...selected]); setShowBulkAdvance(true); }}
            className="px-3 py-2 min-h-[44px] bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] rounded-xl text-xs font-bold flex items-center gap-1.5 cursor-pointer"
          >
            <ArrowRight className="h-3.5 w-3.5" /> Advance (eligible only)
          </button>
          <button
            type="button"
            onClick={() => handleBulkPause(true)}
            disabled={bulkPausing}
            className="px-3 py-2 min-h-[44px] bg-slate-100 hover:bg-slate-200 text-slate-700 disabled:opacity-50 rounded-xl text-xs font-bold flex items-center gap-1.5 cursor-pointer"
          >
            <PauseCircle className="h-3.5 w-3.5" /> Pause emails
          </button>
          <button
            type="button"
            onClick={() => handleBulkPause(false)}
            disabled={bulkPausing}
            className="px-3 py-2 min-h-[44px] bg-slate-100 hover:bg-slate-200 text-slate-700 disabled:opacity-50 rounded-xl text-xs font-bold flex items-center gap-1.5 cursor-pointer"
          >
            <Play className="h-3.5 w-3.5" /> Resume emails
          </button>
          <button
            type="button"
            onClick={handleExportCsv}
            className="px-3 py-2 min-h-[44px] bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold flex items-center gap-1.5 cursor-pointer"
          >
            <Download className="h-3.5 w-3.5" /> Export CSV
          </button>
          <button
            type="button"
            onClick={clearSelection}
            aria-label="Clear selection"
            className="p-2 min-w-[44px] min-h-[44px] text-slate-400 hover:text-slate-800 hover:bg-slate-100 rounded-xl cursor-pointer flex items-center justify-center"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      </>
      )}

      {/* Internal Tasks — designated page (decluttered 2026-10-11) */}
      {pageView === 'tasks' && (
        <InternalTasksSection
          onOpenApplicant={(uid) => setDrawerUid(uid)}
          refreshSignal={sectionsSignal}
        />
      )}

      {/* Redlist — senior-admin-only management (discrete tab, 2026-10-12) */}
      {pageView === 'redlist' && (
        <RedlistSection />
      )}

      {/* Drawer */}
      {drawerUid && (
        <ApplicantDrawer
          uid={drawerUid}
          uids={drawerUids}
          onClose={() => setDrawerUid(null)}
          onNavigate={setDrawerUid}
          onViewApplicant={onViewApplicant}
          onChanged={refreshAll}
        />
      )}

      {/* Bulk advance modal */}
      {showBulkAdvance && bulkUids.length > 0 && (
        <BulkAdvanceModal
          uids={bulkUids}
          onClose={() => { setShowBulkAdvance(false); setBulkUids([]); }}
          onDone={() => { clearSelection(); refreshAll(); }}
        />
      )}

      {/* Mass Phase Change Override Modal */}
      {showMassPhaseModal && (
        <div className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-amber-50/50">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-amber-600 text-white shadow-xs">
                  <ArrowRightLeft className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-sm font-extrabold text-slate-900">Mass Phase Change Override</h3>
                  <p className="text-xs text-slate-500">Forcibly move {selected.size} applicant(s) to a target phase</p>
                </div>
              </div>
              <button aria-label="Close" title="Close"
                type="button"
                onClick={() => setShowMassPhaseModal(false)}
                className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-200 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="p-5 space-y-4 text-xs">
              {/* Explicit Guard Bypass Warning */}
              <div className="p-3.5 rounded-xl bg-amber-50 border border-amber-200/80 text-amber-900 space-y-1">
                <div className="flex items-center gap-2 font-black text-amber-950">
                  <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                  <span>DISREGARDS PRIOR STAGE GUARDS</span>
                </div>
                <p className="text-[11px] leading-relaxed font-medium">
                  This bulk phase change forcibly updates all <strong>{selected.size}</strong> selected applicant(s) to the chosen stage and <strong>bypasses prior stage prerequisites and gate checks</strong>. Every override is audit-logged with your required note below.
                </p>
              </div>

              {/* Target Stage Picker */}
              <div className="space-y-1">
                <label className="block text-xs font-extrabold text-slate-800">Target Phase / Stage *</label>
                <select
                  value={massPhaseTarget}
                  onChange={(e) => setMassPhaseTarget(e.target.value as StandardPhaseKey)}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs font-bold text-slate-800 bg-white shadow-2xs"
                >
                  {STANDARD_PHASES.map((p) => (
                    <option key={p.key} value={p.key}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Mandatory Admin Note */}
              <div className="space-y-1">
                <label className="block text-xs font-extrabold text-slate-800">
                  Mandatory Override Reason / Admin Note *
                </label>
                <textarea
                  rows={3}
                  value={massPhaseNote}
                  onChange={(e) => setMassPhaseNote(e.target.value)}
                  placeholder="Explain why this mass phase change is being executed without gate checks (minimum 10 characters)..."
                  className="w-full p-3 rounded-xl border border-slate-200 text-xs focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                />
                <p className="text-[11px] text-slate-500 font-medium">
                  {massPhaseNote.trim().length < 10
                    ? `${10 - massPhaseNote.trim().length} more characters required`
                    : '✔ Valid override note'}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50">
              <button
                type="button"
                onClick={() => setShowMassPhaseModal(false)}
                className="px-4 py-2 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-600 hover:bg-slate-100 cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleExecuteMassPhaseChange}
                disabled={submittingMassPhase || massPhaseNote.trim().length < 10}
                className="px-4 py-2 rounded-xl bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-xs font-extrabold cursor-pointer shadow-2xs flex items-center gap-1.5"
              >
                {submittingMassPhase ? 'Executing...' : 'Execute Mass Phase Change'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk nudge modal */}
      {showBulkNudge && (
        <BulkNudgeModal
          items={[...selected].map((uid) => ({ uid, applicationId: selectedRecords.get(uid)?.applicationId || '', displayName: selectedRecords.get(uid)?.displayName, email: selectedRecords.get(uid)?.email }))}
          onClose={() => setShowBulkNudge(false)}
          onSent={() => { clearSelection(); refreshAll(); }}
        />
      )}

      {/* Workflow settings panel (Phase 4, spec §15.3) */}
      {settingsOpen && <WorkflowSettingsPanel onClose={() => setSettingsOpen(false)} />}

      {/* Assign Automation Modal */}
      <AssignAutomationModal
        isOpen={assignAutoOpen}
        onClose={() => setAssignAutoOpen(false)}
        source="workflow"
        applicantIds={assignAutoApplicantIds}
        referenceApplicant={selectedApplicantForAuto ? {
          id: selectedApplicantForAuto.applicationId || selectedApplicantForAuto.id,
          applicantName: selectedApplicantForAuto.applicantName,
          customApplicationId: selectedApplicantForAuto.customApplicationId || selectedApplicantForAuto.applicationId,
          programName: selectedApplicantForAuto.programName || selectedApplicantForAuto.program,
          currentStage: selectedApplicantForAuto.stage || selectedApplicantForAuto.currentStage,
        } : null}
        onSuccess={refreshAll}
      />
    </div>
  );
};
